import type { CatalogData } from '#root/api/clients/product/data-contracts'
import titleTemplate from '#core/utils/seo/titleTemplate'

/**
 * Derives PLP SEO metadata from the catalog payload and current route state.
 *
 * The contract requires both `data.indexable` and
 * `pageMetaData.robots.content` to agree before a filtered/category page is treated
 * as indexable. Non-indexable pages are normalized to `noindex,nofollow` and do
 * not emit unique SEO metadata.
 */
export function usePlpSeoMeta({
  data,
  hasFilters,
  page,
  filters
}: {
  data: MaybeRefOrGetter<CatalogData>
  page: number
  hasFilters: MaybeRefOrGetter<boolean>
  filters?: MaybeRefOrGetter<string | undefined>
}) {
  const { $seo, $t } = useNuxtApp()
  const host = useHost()
  const { seoMetaImage } = useAppConfig().pages.plp
  const { brand } = useRuntimeConfig().public
  const indexableQueryParam = useRouteQuery('indexable')

  const dataValue = computed(() => toValue(data))
  const url = computed(() => dataValue.value?.url)

  const fallbacks = computed(() => {
    const seoPlaceholderValues = {
      brand: $t.brand,
      brandName: $t.brandName,
      collectionName: dataValue.value?.title || ''
    }
    return {
      title: dataValue.value && replaceAll($seo.plp!.title, seoPlaceholderValues),
      description: dataValue.value && replaceAll($seo.plp!.description, seoPlaceholderValues)
    }
  })

  const robotsApiContent = computed(() => getProductMetaTagStringValue(dataValue.value?.pageMetaData?.robots)?.trim())
  // A missing robots field is not an override; only an explicit `noindex` is.
  const isRobotsIndexable = computed(() => !robotsApiContent.value?.toLowerCase()?.includes('noindex'))

  const hasIndexableSignal = computed(() => dataValue.value?.seo?.indexable || false)
  const hasIndexableQueryParam = computed(() => indexableQueryParam.value === 'true')

  const isPageIndexable = computed(() => {
    if (typeof dataValue.value?.seo?.indexable === 'boolean') {
      return hasIndexableSignal.value
    }

    if (robotsApiContent.value) {
      return !toValue(hasFilters) && isRobotsIndexable.value
    }

    return !toValue(hasFilters)
  })

  const canonicalHref = computed(() => {
    if (!url.value) return undefined

    const params = new URLSearchParams()

    if (page > 1) params.set('page', String(page))

    if (hasIndexableSignal.value || hasIndexableQueryParam.value) {
      const filtersValue = toValue(filters)
      if (filtersValue) params.set('filters', filtersValue)
    }

    if (hasIndexableQueryParam.value) params.set('indexable', 'true')

    const qs = params.toString()
    return `https://${host}${url.value}${qs ? `?${qs}` : ''}`
  })
  const paginationLabel = computed(() => {
    const hasMultiplePages = (dataValue.value?.total || 0) > (dataValue.value?.items || 0)
    const isPagingApplied = page > 1

    return hasMultiplePages && isPagingApplied ? replaceAll($t.pageNumber, { page }) : undefined
  })
  const derivedTitle = computed(() => {
    if (!dataValue.value?.title) return undefined

    const titleSegments = [
      dataValue.value.title,
      paginationLabel.value,
    ].filter(Boolean)

    return titleSegments.length > 1 ? titleSegments.join(' - ') : undefined
  })
  const derivedSeoTitle = computed(() => (
    derivedTitle.value ? titleTemplate($t, derivedTitle.value) : undefined
  ))

  const seoTitle = computed(() => (
    derivedSeoTitle.value || getProductMetaTagStringValue(dataValue.value?.pageMetaData?.title) || fallbacks.value.title
  ))
  const seoDescription = computed(() => (
    getProductMetaTagStringValue(dataValue.value?.pageMetaData?.description) || fallbacks.value.description
  ))
  const ogTitle = computed(() => (
    derivedSeoTitle.value || getProductMetaTagStringValue(dataValue.value?.pageMetaData?.['og:title']) || seoTitle.value
  ))
  const ogDescription = computed(() => (
    isPageIndexable.value
      ? getProductMetaTagStringValue(dataValue.value?.pageMetaData?.['og:description']) || seoDescription.value
      : undefined
  ))
  const seoH1 = computed(() => derivedTitle.value || dataValue.value?.seoTags?.h1)
  const displayCategoryTitle = computed(() => seoH1.value || dataValue.value?.title)

  const robots = computed(() => {
    if (isPageIndexable.value) {
      if (typeof dataValue.value?.seo?.indexable === 'boolean') return hasIndexableSignal.value ? 'index,follow' : 'noindex,nofollow'
      if (robotsApiContent.value) return robotsApiContent.value
      if (!toValue(hasFilters)) return 'index,follow'
    }

    return 'noindex,nofollow'
  })

  const meta = computed(() => {
    const metaEntries = [{
      name: 'robots',
      content: robots.value
    }]

    if (seoDescription.value) {
      metaEntries.push({
        name: 'description',
        content: seoDescription.value
      })
    }

    if (!isPageIndexable.value) return metaEntries

    if (ogTitle.value) {
      metaEntries.push({
        name: 'og:title',
        content: ogTitle.value
      })
    }

    if (ogDescription.value) {
      metaEntries.push({
        name: 'og:description',
        content: ogDescription.value
      })
    }

    if (seoMetaImage) {
      metaEntries.push(
        {
          name: 'og:image',
          content: seoMetaImage
        },
        {
          name: 'og:type',
          content: 'website'
        }
      )
    }

    if (url.value && host) {
      metaEntries.push({
        name: 'og:url',
        content: `https://${host}${url.value}`
      })
    }

    metaEntries.push({
      name: 'twitter:card',
      content: 'summary_large_image'
    })

    if (brand) {
      metaEntries.push(
        {
          name: 'twitter:creator',
          content: `@${brand}`
        },
        {
          name: 'twitter:site',
          content: `@${brand}`
        }
      )
    }

    return metaEntries
  })

  const link = computed(() => canonicalHref.value
    ? [{ rel: 'canonical', href: canonicalHref.value }]
    : [])

  return {
    displayCategoryTitle,
    link,
    meta,
    seoTitle,
  }
}
